import { useCallback, useState, useMemo } from 'react';
import { DND_TYPES } from './types';
import { Provider } from 'react-redux';
import { createStore } from './BulkActions/redux/createStore';
import { StoreContext } from './BulkActions/redux/storeContext';
import { getIsBoardEmpty, getIsPipelineLoading } from './utils';
import DroppableBoard from './dnd-components/DroppableBoard';
import styled from 'styled-components';
import Empty from './Empty';
import { useConfigs } from './lib/config-context';
import ColumnWithIntersectionObserver from './ColumnWithIntersectionObserver';
import BulkActionsContainer from './BulkActions/components/BulkActionsContainer';
import { Flex } from 'foundations-components/components/Flex';
import { ScrollOverhang } from 'foundations-components/transitional/components/ScrollOverhang';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BulkActionsAndBoardContainer = styled.div.withConfig({
  displayName: "Board__BulkActionsAndBoardContainer",
  componentId: "sc-1ur053d-0"
})(({
  theme,
  useReskin
}) => `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  height: 100%;
  width: 100%;
  background-color: ${useReskin ? theme.color['fill-surface-default'] : 'unset'};
`);
const Board = ({
  BulkActionsComponent,
  columns,
  isBulkActionsRedesignEnabled,
  onColumnChange
}) => {
  const {
    setIsDragInProgress,
    useReskin
  } = useConfigs();
  // This ensures that the reference is stable as long as the component is mounted.
  // useMemo may throw away a reference in the future so it should not be used here.
  const [store] = useState(createStore);
  const sortedColumns = useMemo(() => {
    if (columns) {
      return columns;
    }
    return [];
  }, [columns]);
  const handleCardDragEnd = useCallback((source, destination, itemId) => {
    const hasColumnChangeHandler = typeof onColumnChange === 'function';
    if (!hasColumnChangeHandler) {
      setIsDragInProgress(false);
      return;
    }
    onColumnChange(source, destination, () => {
      setIsDragInProgress(false);
    }, itemId);
  }, [onColumnChange, setIsDragInProgress]);
  const handleDragStart = useCallback(start => {
    if (start.type === DND_TYPES.BOARD_CARD) {
      setIsDragInProgress(true);
    }
  }, [setIsDragInProgress]);
  const handleDragEnd = useCallback(result => {
    // dropped nowhere
    if (!result.destination) {
      setIsDragInProgress(false);
      return;
    }
    const source = result.source;
    const destination = result.destination;
    const itemId = result.draggableId;

    // did not move cards nor columns anywhere - can bail early
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      setIsDragInProgress(false);
      return;
    }

    // reordering card & bail
    if (result.type === DND_TYPES.BOARD_CARD) {
      handleCardDragEnd(source, destination, itemId);
      return;
    }
  }, [setIsDragInProgress, handleCardDragEnd]);
  const isBoardEmpty = useMemo(() => getIsBoardEmpty(sortedColumns), [sortedColumns]);
  const isPipelineLoading = useMemo(() => getIsPipelineLoading(sortedColumns), [sortedColumns]);
  const generateColumns = useCallback(() => sortedColumns.map(columnData => {
    return columnData ? /*#__PURE__*/_jsx(ColumnWithIntersectionObserver, {
      columnId: columnData.id,
      columnData: columnData,
      isBoardEmpty: isBoardEmpty
    }, columnData.id) : null;
  }), [isBoardEmpty, sortedColumns]);

  // This gets us all cardIds that are rendered on the page
  const allCardIds = useMemo(() => sortedColumns.flatMap(stage => stage.cards.map(card => card.id)), [sortedColumns]);

  // This gets us the total number of cards available. Not just what is rendered
  const totalCards = useMemo(() => sortedColumns.reduce((acc, curr) => acc + (curr && curr.totalCards || 0), 0), [sortedColumns]);
  return /*#__PURE__*/_jsx(Provider, {
    store: store,
    context: StoreContext,
    children: /*#__PURE__*/_jsxs(BulkActionsAndBoardContainer, {
      useReskin: Boolean(useReskin),
      children: [/*#__PURE__*/_jsx(BulkActionsContainer, {
        BulkActionsComponent: BulkActionsComponent,
        allCardIds: allCardIds || [],
        totalCards: totalCards,
        isBulkActionsRedesignEnabled: isBulkActionsRedesignEnabled
      }), /*#__PURE__*/_jsx(ScrollOverhang, {
        children: /*#__PURE__*/_jsxs(DroppableBoard, {
          onDragStart: handleDragStart,
          onDragEnd: handleDragEnd,
          children: [/*#__PURE__*/_jsx(Flex, {
            as: "div",
            blockSize: isBoardEmpty ? 'initial' : '100%',
            "data-test-id": "cdb-columns-container",
            children: generateColumns()
          }), isBoardEmpty && !isPipelineLoading && /*#__PURE__*/_jsx(Empty, {})]
        })
      })]
    })
  });
};
export default Board;